import { Injectable, OnDestroy } from '@angular/core';

import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import {
  bufferCount,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { TPoint, TPoints } from 'src/app/project/modules/points/points.model';
import { TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';

import { PointsService } from 'src/app/project/modules/points/points.service';
import { ActiveService } from 'src/app/project/services/active/active.service';
import { PlanDataService, TPlanData } from '../plan/plan-data.service';
import { PlanPinsService } from '../plan/plan-pins.service';
import { SiteTableColumnsDataService } from '../site/site-table/site-table-columns-data.service';
import { SiteService } from '../site/site.service';

import clone from 'lodash/clone';
import isEqual from 'lodash/isEqual';
import { logEventInGTAG } from '../../services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventFiltering,
} from '../../services/analytics/google-analytics.consts';
import { EStore } from '../../shared/enums/store.enum';
import { EPlanModule } from '../plan/plan-module.enum';
import { PointSearchService } from '../points/point-search/point-search.service';
import { getSelectedPoints, removeSelectedPoint } from '../points/selected-points';
import { CustomTableService } from '../site/site-table/custom-table/custom-table.service';
import { checkCustomWorkspaceId } from '../workspace/workspace';
import { EWorkspaces } from '../workspace/workspaces.enum';
import { checkLocation } from './filter-logic/filter-location';

const CUSTOM_WORKSPACES: string[] = [
  EWorkspaces.OVERVIEW,
  EWorkspaces.REMINDERS,
  EWorkspaces.TIMELINE,
];

@Injectable({
  providedIn: 'root',
})
export class SitePointFilterService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private plan: TPlanData = this.planDataService.getPlan();

  private workspaces$ = new Observable<TWorkspacesById>();
  workspaceId: string;

  private pointsByWorkspace$: Observable<TPoints>;
  pointsByWorkspace: TPoints;

  filterTrigger$ = new Subject<{
    _keepScrollPosition?: boolean;
  }>();

  constructor(
    private store: Store<{
      points: TPoints;
      workspaces: TWorkspacesById;
    }>,
    private siteTableColumnsDataService: SiteTableColumnsDataService,
    private planDataService: PlanDataService,
    private activeService: ActiveService,
    private pointsService: PointsService,
    private siteService: SiteService,
    private planPinsService: PlanPinsService,
    private customTableService: CustomTableService,
    private pointSearchService: PointSearchService,
  ) {
    this.workspaces$ = this.store.pipe(select(EStore.WORKSPACES));

    this.workspaces$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.workspaceId = checkCustomWorkspaceId() || this.activeService.getActiveWorkspaceId();
    });

    this.filterTrigger$
      .pipe(
        switchMap((data) =>
          this.filterPointsInObservable(data).pipe(
            bufferCount(1), // don't cache previous calls
            debounceTime(300), // if another request in 300ms ignore previous ones
          ),
        ),
      )
      .subscribe(); // cancel previous filtering and start a new one when trigger is called

    this.pointsByWorkspace$ = this.store.pipe(select(EStore.POINTS));

    this.pointsByWorkspace$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        tap((pointsByWorkspace) => (this.pointsByWorkspace = pointsByWorkspace)),
      )
      .subscribe(() => {
        this.filterPoints();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  filterPoints({ _keepScrollPosition = true }: { _keepScrollPosition?: boolean } = {}): void {
    if (CUSTOM_WORKSPACES.includes(this.workspaceId)) {
      return;
    }

    this.filterTrigger$.next({
      _keepScrollPosition,
    });
  }

  private filterPointsInObservable({
    _keepScrollPosition = false,
  }: { _keepScrollPosition?: boolean } = {}): Observable<void> {
    return new Observable<void>((subscriber) => {
      const workspaceId = checkCustomWorkspaceId() || this.activeService.getActiveWorkspaceId();

      this.siteTableColumnsDataService.clearColumns({ totalCost: true });

      const points = this.filterAdvancedWorkspacePoints(workspaceId);

      this.updateTable(points, _keepScrollPosition);
      this.checkSelectedPoints(points);

      this.planPinsService.updatePointMarkers(EPlanModule.SITE);

      const hasPoints = this.pointSearchService.getTotalCount(workspaceId) > 0;

      if (hasPoints) {
        this.siteService.setTableFetched(true);
      }

      subscriber.next();
      subscriber.complete();
    });
  }

  filterAdvancedWorkspacePoints(workspaceId: string): TPoint[] {
    logEventInGTAG(EGoogleEventFiltering.APPLY_ADVANCED_FILTER, {
      source: EGoogleEventCategory.FILTERING,
    });

    const workspacePoints = this.pointsByWorkspace[workspaceId]?.entities;

    if (!workspacePoints) {
      return [];
    }

    const searchedPoints = this.pointSearchService.getData(workspaceId);

    const matchedPoints = searchedPoints
      .map((searchedPoint) => workspacePoints.find((point) => point._id === searchedPoint._id))
      .filter(Boolean);

    const points = matchedPoints.filter((point) => checkLocation(point, this.plan));

    this.pointSearchService.setTotalCount(points.length, workspaceId);
    this.pointsService.setMapPoints(points);

    return points;
  }

  filterPointsFromSavedView({
    _keepScrollPosition = false,
  }: { _keepScrollPosition?: boolean } = {}): void {
    this.filterPoints({ _keepScrollPosition });
  }

  private checkSelectedPoints(points: TPoint[]): void {
    const selectedPoints = clone(getSelectedPoints());

    selectedPoints.forEach((pointId) => {
      const pointFound = points.find((point) => point._id === pointId);

      if (!pointFound) {
        removeSelectedPoint(pointId);
      }
    });
  }

  private updateTable(points: TPoint[], _keepScrollPosition: boolean): void {
    const table = this.customTableService.getTable();

    if (table) {
      if (table.hasSelectedAllPoints) {
        table.setSelectedPointsCount(points.length);
      }

      table.updatePoints(points);
      table.load({ keepScrollPosition: _keepScrollPosition });
    }
  }
}
